import { RoomEvent, Track } from 'livekit-client';
import type { RemoteParticipant, RemoteTrackPublication, Room } from 'livekit-client';

/**
 * Logs de diagnóstico do ciclo de inscrição em tracks remotas.
 *
 * Sem isso, "não ouço o fulano" não deixa rastro no console/telemetria: a app não ouvia
 * `TrackSubscriptionFailed`, `TrackStreamStateChanged` (SFU pausou a track) nem
 * `TrackSubscriptionStatusChanged`. Só loga — quem recupera é o remote-audio-watchdog.
 */
export function attachSubscriptionDiagnostics(room: Room): () => void {
  const onSubscriptionFailed = (
    trackSid: string,
    participant: RemoteParticipant,
    reason?: unknown,
  ) => {
    console.warn('[subscription] TrackSubscriptionFailed', {
      trackSid,
      participant: participant.identity,
      reason,
    });
  };

  const onStreamStateChanged = (
    pub: RemoteTrackPublication,
    streamState: Track.StreamState,
    participant: RemoteParticipant,
  ) => {
    if (pub.kind !== Track.Kind.Audio) return;
    // Áudio pausado pelo SFU (não deveria acontecer — adaptiveStream só pausa vídeo).
    if (streamState === Track.StreamState.Paused) {
      console.warn('[subscription] áudio remoto pausado pelo SFU', {
        trackSid: pub.trackSid,
        participant: participant.identity,
      });
    }
  };

  const onSubscriptionStatusChanged = (
    pub: RemoteTrackPublication,
    status: unknown,
    participant: RemoteParticipant,
  ) => {
    if (pub.kind !== Track.Kind.Audio) return;
    console.info('[subscription] status de áudio remoto', {
      trackSid: pub.trackSid,
      participant: participant.identity,
      status,
    });
  };

  const onUnsubscribed = (
    _track: unknown,
    pub: RemoteTrackPublication,
    participant: RemoteParticipant,
  ) => {
    if (pub.kind !== Track.Kind.Audio) return;
    console.info('[subscription] áudio remoto desinscrito', {
      trackSid: pub.trackSid,
      participant: participant.identity,
      roomState: room.state,
    });
  };

  room.on(RoomEvent.TrackSubscriptionFailed, onSubscriptionFailed);
  room.on(RoomEvent.TrackStreamStateChanged, onStreamStateChanged);
  room.on(RoomEvent.TrackSubscriptionStatusChanged, onSubscriptionStatusChanged);
  room.on(RoomEvent.TrackUnsubscribed, onUnsubscribed);

  return () => {
    room.off(RoomEvent.TrackSubscriptionFailed, onSubscriptionFailed);
    room.off(RoomEvent.TrackStreamStateChanged, onStreamStateChanged);
    room.off(RoomEvent.TrackSubscriptionStatusChanged, onSubscriptionStatusChanged);
    room.off(RoomEvent.TrackUnsubscribed, onUnsubscribed);
  };
}
